import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  KTextInput,
  KButton,
  KIcon,
} from "@kyburz/kdesign-system";
import useSetState from "../hooks/useSetState";

import { getDevices, getDevicesByCustomer } from "../apiCalls/config/devices";
import { setAlert } from "../redux/actions/actions";
import KInfiniteTable from "./table/KInfiniteTable";

const MemoizedKInfiniteTable = React.memo(
  (props: any) => (
    <KInfiniteTable
      data={props.data}
      hasNextPage={props.hasNextPage}
      isNextPageLoading={props.isNextPageLoading}
      loadNextPage={props.loadNextPage}
      columns={props.columns}
      hasStickyColumns={true}
    />
  ),
  (oldProps, newProps) => {
    return oldProps.data === newProps.data;
  },
);

interface Props {
  withCTA?: boolean;
  handleAddDevice?: (value) => void | any;
  handleEditClick?: (value) => void | any;
  handleDeleteClick?: (value, value1) => void | any;
  handleSelectDevice?: (value, value1) => void | any;
  company?: string;
}

const DevicesTable = ({
  withCTA = true,
  handleAddDevice = undefined,
  handleEditClick = undefined,
  handleDeleteClick = undefined,
  handleSelectDevice = undefined,
  company = "",
}: Props) => {
  const dispatch = useDispatch();

  const [
    { query, pagesLoaded, hasNextPage, isNextPageLoading, items },
    setState,
  ] = useSetState({
    query: "",
    pagesLoaded: 0,
    hasNextPage: true,
    isNextPageLoading: false,
    items: [],
  });

  useEffect(() => {
    setState({ isNextPageLoading: true });
    if (company) {
      getDevicesByCustomer(company, query, 1)
        .then((res) => {
          if (res && res.data && res.data.data) {
            setState({
              items: res.data.data.map((x) => ({
                imei: x.imei,
                customer: x.customer,
                vehicle_uuid: x.vehicle_uuid,
                template: x.template,
                hardware: x.hardware_type,
              })),
              isNextPageLoading: false,
              pagesLoaded: 1,
            });
          }
        })
        .catch((e) => {
          dispatch(setAlert(e.detail, "error"));
        });
    } else {
      getDevices(query, 1, null, "")
        .then((res) => {
          if (res && res.data && res.data.data) {
            setState({
              items: res.data.data.map((x) => ({
                imei: x.imei,
                customer: x.customer,
                vehicle_uuid: x.vehicle_uuid,
                template: x.template,
                hardware: x.hardware_type,
              })),
              isNextPageLoading: false,
              pagesLoaded: 1,
            });
          }
        })
        .catch((e) => {
          dispatch(setAlert(e.detail, "error"));
        });
    }
  }, [query]);

  const loadNextPage = () => {
    setState({ isNextPageLoading: true });

    if (company) {
      getDevicesByCustomer(company, query, pagesLoaded + 1)
        .then((res) => {
          if (res && res.data && res.data.data) {
            setState({
              items: [...items].concat(
                ...res.data.data.map((x) => ({
                  imei: x.imei,
                  customer: x.customer,
                  vehicle_uuid: x.vehicle_uuid,
                  template: x.template,
                  hardware: x.hardware_type,
                })),
              ),
              isNextPageLoading: false,
              pagesLoaded: pagesLoaded + 1,
            });
          }
        })
        .catch((e) => {
          dispatch(setAlert(e.detail, "error"));
        });
    } else {
      getDevices(query, pagesLoaded + 1, null, "")
        .then((res) => {
          if (res && res.data && res.data.data) {
            setState({
              items: [...items].concat(
                ...res.data.data.map((x) => ({
                  imei: x.imei,
                  customer: x.customer,
                  vehicle_uuid: x.vehicle_uuid,
                  template: x.template,
                  hardware: x.hardware_type,
                })),
              ),
              isNextPageLoading: false,
              pagesLoaded: pagesLoaded + 1,
            });
          }
        })
        .catch((e) => {
          dispatch(setAlert(e.detail, "error"));
        });
    }
  };

  const handleChange = (e) => setState({ [e.target.name]: e.target.value });

  let columns = [
    {
      Header: "IMEI",
      accessor: "imei",
    },
    {
      Header: "Customer",
      accessor: "customer",
    },
    {
      Header: "Assigned Vehicle",
      accessor: "vehicle_uuid",
      width: 350,
    },
    {
      Header: "Template",
      accessor: "template",
    },
    {
      Header: "Hardware Type",
      accessor: "hardware",
    },
    {
      Header: "SIM Type",
      accessor: "sim_type",
    },
    {
      Header: "Actions",
      Cell: (row) => {
        return (
          <div style={{ textAlign: "center" }}>
            {handleEditClick === undefined ? null : (
              <div
                style={{ display: "inline-block", marginRight: "16px" }}
                onClick={() => handleEditClick(row.row.original)}
              >
                <KIcon icon="eye" color="#383F51" size={20} />
              </div>
            )}
            {handleDeleteClick === undefined ? null : (
              <div
                style={{ display: "inline-block" }}
                onClick={(e) => handleDeleteClick(e, row.row.original)}
              >
                <KIcon icon="trash-2" color="#383F51" size={20} />
              </div>
            )}
            {handleSelectDevice === undefined ? null : (
              <KButton
                onClick={(e) => handleSelectDevice(e, row.row.original)}
                children="Select"
                theme="primary"
                size="small"
                style={{ marginTop: 0 }}
              />
            )}
          </div>
        );
      },
      width: 100,
      sticky: "right",
    },
  ];

  columns = React.useMemo(() => columns, []);

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <KTextInput
          placeholder="Search devices..."
          value={query}
          onChange={handleChange}
          name="query"
        />
        {withCTA ? (
          <KButton
            theme="primary"
            children="New Device"
            icon={<KIcon icon="plus"/>}
            onClick={handleAddDevice}
          />
        ) : null}
      </div>
      <div style={{ margin: "16px 0 0 0", height: 500 }}>
        <MemoizedKInfiniteTable
          isNextPageLoading={isNextPageLoading}
          hasNextPage={hasNextPage}
          columns={columns}
          data={items}
          loadNextPage={loadNextPage}
        />
      </div>
    </>
  );
};

export default DevicesTable;
